/**
 * {@link CloseScoreReview} — review screen shown when an admin initiates the
 * transition from "active" to "complete". Displays participants with missing
 * station entries and offers cancel or confirm (auto-apply DNS) actions.
 *
 * Requirements: 12.1, 12.2, 12.3
 */

import { useState, useEffect, useCallback } from 'react';
import { Button, ErrorBanner, Spinner } from '@scoring/frontend-shared';
import type { ApiClient } from '@scoring/frontend-shared';
import type { CSSProperties } from 'react';

export interface MissingEntry {
  participantId: string;
  participantName: string;
  teamName: string | null;
  missingStations: number[];
}

export interface CloseScoreReviewProps {
  eventId: string;
  apiClient: ApiClient;
  /** Called when the admin cancels — return to active state. */
  onCancel: () => void;
  /** Called after DNS is applied and the event transitions to complete. */
  onConfirm: () => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.4)',
  zIndex: 1000,
};

const dialogStyle: CSSProperties = {
  background: 'var(--color-bg-surface, #FFFFFF)',
  borderRadius: 'var(--radius-button, 8px)',
  padding: '1.5rem',
  maxWidth: '36rem',
  width: '90%',
  maxHeight: '80vh',
  overflow: 'auto',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

const titleStyle: CSSProperties = {
  margin: '0 0 0.5rem',
  fontSize: '1.25rem',
  fontWeight: 600,
};

const descriptionStyle: CSSProperties = {
  margin: '0 0 1rem',
  color: 'var(--color-text-secondary, #6B7280)',
  lineHeight: 1.5,
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  marginBottom: '1.5rem',
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem',
  borderBottom: '2px solid var(--color-border, #E5E7EB)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1A1A2E)',
};

const tdStyle: CSSProperties = {
  padding: '0.5rem',
  borderBottom: '1px solid var(--color-border, #E5E7EB)',
  color: 'var(--color-text-primary, #1A1A2E)',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: '0.75rem',
  justifyContent: 'flex-end',
};

const noMissingStyle: CSSProperties = {
  padding: '1rem',
  background: '#dafbe1',
  borderRadius: 'var(--radius-button, 8px)',
  color: 'var(--color-brand-green, #1B4332)',
  marginBottom: '1.5rem',
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
};

export function CloseScoreReview({
  eventId,
  apiClient,
  onCancel,
  onConfirm,
}: CloseScoreReviewProps): JSX.Element {
  const [missingEntries, setMissingEntries] = useState<MissingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const fetchMissingEntries = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.request<{ missingEntries: MissingEntry[] }>(
        'GET',
        `/events/${encodeURIComponent(eventId)}/missing-entries`,
      );
      setMissingEntries(data.missingEntries);
      setError(null);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load missing entries.');
    } finally {
      setLoading(false);
    }
  }, [apiClient, eventId]);

  useEffect(() => {
    void fetchMissingEntries();
  }, [fetchMissingEntries]);

  async function handleConfirm(): Promise<void> {
    setConfirming(true);
    setError(null);
    try {
      // Apply DNS to all missing entries and transition to complete
      await apiClient.request<unknown>(
        'POST',
        `/events/${encodeURIComponent(eventId)}/apply-dns`,
      );
      onConfirm();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to apply DNS and close scoring.');
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div style={overlayStyle} aria-hidden="true" onClick={onCancel}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="close-review-title"
        aria-describedby="close-review-desc"
        tabIndex={-1}
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="close-review-title" style={titleStyle}>
          Close Scoring Review
        </h2>
        <p id="close-review-desc" style={descriptionStyle}>
          Review participants with missing station entries before completing the event.
          Confirming will automatically apply DNS (Did Not Shoot) to all missing entries.
        </p>

        {error && (
          <ErrorBanner title="Error" onDismiss={() => setError(null)}>
            {error}
          </ErrorBanner>
        )}

        {loading ? (
          <Spinner label="Loading missing entries…" />
        ) : missingEntries.length === 0 ? (
          <div style={noMissingStyle}>
            All participants have complete station entries. You can safely close scoring.
          </div>
        ) : (
          <table style={tableStyle} aria-label="Participants with missing station entries">
            <thead>
              <tr>
                <th style={thStyle}>Participant</th>
                <th style={thStyle}>Team</th>
                <th style={thStyle}>Missing Stations</th>
              </tr>
            </thead>
            <tbody>
              {missingEntries.map((entry) => (
                <tr key={entry.participantId}>
                  <td style={tdStyle}>{entry.participantName}</td>
                  <td style={tdStyle}>{entry.teamName ?? '—'}</td>
                  <td style={tdStyle}>
                    {entry.missingStations.join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && (
          <div style={actionsStyle}>
            <Button variant="secondary" onClick={onCancel} disabled={confirming}>
              Cancel
            </Button>
            <Button
              onClick={() => { void handleConfirm(); }}
              disabled={confirming}
            >
              {confirming ? (
                <Spinner size="1rem" label="Applying DNS…" />
              ) : missingEntries.length > 0 ? (
                'Confirm & Apply DNS'
              ) : (
                'Confirm & Complete'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
